import { createContext, useContext, useEffect, useState } from "react";
import { api, setAuth, clearAuth, getStoredUser } from "@/lib/api";

const AuthContext = createContext(null);
const ACTIVE_CLIENT_KEY = "hbt_active_client";
// When an admin starts "View as client", we stash the original admin token /
// refresh / user under these keys and swap the live token to the impersonation
// one. On exit, we restore from here. All localStorage so the banner survives
// a hard refresh.
const ADMIN_TOKEN_STASH = "hbt_admin_token_stash";
const ADMIN_REFRESH_STASH = "hbt_admin_refresh_stash";
const ADMIN_USER_STASH = "hbt_admin_user_stash";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser());
  const [impersonation, setImpersonation] = useState(null); // {impersonator_id, impersonator_name, client_id, client_name}
  const [loading, setLoading] = useState(true);
  const [activeClientId, setActiveClientIdState] = useState(() => localStorage.getItem(ACTIVE_CLIENT_KEY) || "");

  const setActiveClientId = (id) => {
    setActiveClientIdState(id || "");
    if (id) localStorage.setItem(ACTIVE_CLIENT_KEY, id);
    else localStorage.removeItem(ACTIVE_CLIENT_KEY);
  };

  const userClientIds = (u) => {
    const list = [...(u?.client_ids || [])];
    if (u?.client_id && !list.includes(u.client_id)) list.unshift(u.client_id);
    return list;
  };

  const hasCap = (cap) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.role === "staff") return Array.isArray(user.capabilities) && user.capabilities.includes(cap);
    return false;
  };

  useEffect(() => {
    const t = localStorage.getItem("hbt_token");
    if (!t) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        setImpersonation(r.data?.impersonation || null);
        localStorage.setItem("hbt_user", JSON.stringify(r.data));
        // if active_client isn't a member anymore, clear it
        const ids = userClientIds(r.data);
        if (activeClientId && ids.length && !ids.includes(activeClientId)) {
          setActiveClientId("");
        }
      })
      .catch(() => clearAuth())
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAuth(data.token, data.refresh_token, data.user);
    setUser(data.user);
    setImpersonation(null);
    // Reset any stale active-client scope on new login
    setActiveClientId("");
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data.user;
  };

  const logout = async () => {
    const refresh_token = localStorage.getItem("hbt_refresh");
    if (refresh_token) {
      try {
        await api.post("/auth/logout", { refresh_token });
      } catch (err) {
        console.warn("Logout revoke failed (token may already be expired)", err?.message);
      }
    }
    clearAuth();
    // Clear any lingering impersonation stash so the next login starts clean.
    localStorage.removeItem(ADMIN_TOKEN_STASH);
    localStorage.removeItem(ADMIN_REFRESH_STASH);
    localStorage.removeItem(ADMIN_USER_STASH);
    setActiveClientId("");
    setImpersonation(null);
    setUser(null);
  };

  // ----- Admin "View as client" (impersonation) -----
  // startViewAs stashes the admin's current token + refresh + user, requests a
  // scoped view-as token, swaps it in, then hard-navigates to /app so every
  // page re-queries with the new identity.
  const startViewAs = async (clientId) => {
    const adminToken = localStorage.getItem("hbt_token");
    const adminRefresh = localStorage.getItem("hbt_refresh");
    const adminUser = localStorage.getItem("hbt_user");
    if (!adminToken) throw new Error("Not signed in.");
    const { data } = await api.post("/auth/view-as", { client_id: clientId });
    // Stash admin session so we can restore on exit.
    localStorage.setItem(ADMIN_TOKEN_STASH, adminToken);
    if (adminRefresh) localStorage.setItem(ADMIN_REFRESH_STASH, adminRefresh);
    if (adminUser) localStorage.setItem(ADMIN_USER_STASH, adminUser);
    // Swap live token to the impersonation token. No refresh token for view-as
    // sessions — when it expires, the admin is bounced back to their own view.
    localStorage.setItem("hbt_token", data.token);
    localStorage.removeItem("hbt_refresh");
    setActiveClientId("");
    // Hard reload so all in-flight components refetch with the new scope.
    window.location.assign("/app");
  };

  const stopViewAs = () => {
    const stashedToken = localStorage.getItem(ADMIN_TOKEN_STASH);
    const stashedRefresh = localStorage.getItem(ADMIN_REFRESH_STASH);
    const stashedUser = localStorage.getItem(ADMIN_USER_STASH);
    if (!stashedToken) {
      // Safety net: nothing to restore, force a login.
      clearAuth();
      window.location.assign("/login");
      return;
    }
    localStorage.setItem("hbt_token", stashedToken);
    if (stashedRefresh) localStorage.setItem("hbt_refresh", stashedRefresh);
    if (stashedUser) localStorage.setItem("hbt_user", stashedUser);
    localStorage.removeItem(ADMIN_TOKEN_STASH);
    localStorage.removeItem(ADMIN_REFRESH_STASH);
    localStorage.removeItem(ADMIN_USER_STASH);
    setImpersonation(null);
    window.location.assign("/app/clients");
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      activeClientId, setActiveClientId,
      userClientIds: userClientIds(user),
      hasCap,
      impersonation, startViewAs, stopViewAs,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
